/**
 * Full sortable + paginated observation data table.
 */

import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Eye } from 'lucide-react';
import type { DashboardObservation } from '@/lib/dashboard/types';
import { ObservationDrawer } from './ObservationDrawer';

type SortKey = 'observationDate' | 'siteName' | 'observerName' | 'childrenPresent' | 'totalAverage' | 'scoredItems' | 'naItems';
type SortDir = 'asc' | 'desc';

const PAGE_SIZES = [10, 25, 50] as const;

function scoreBadge(score: number | null) {
  if (score === null) return <span className="text-muted-foreground">—</span>;
  let cls = 'text-yellow-700 bg-yellow-50';
  if (score >= 5) cls = 'text-green-700 bg-green-50';
  else if (score < 3) cls = 'text-red-700 bg-red-50';
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>
      {score.toFixed(2)}
    </span>
  );
}

export function FullDataTable({ observations }: { observations: DashboardObservation[] }) {
  const [sortKey, setSortKey] = useState<SortKey>('observationDate');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [selected, setSelected] = useState<DashboardObservation | null>(null);

  const sorted = useMemo(() => {
    const copy = [...observations];
    copy.sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [observations, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paginated = sorted.slice(page * pageSize, (page + 1) * pageSize);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(0);
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return null;
    return sortDir === 'asc' ? (
      <ChevronUp className="inline h-3 w-3" />
    ) : (
      <ChevronDown className="inline h-3 w-3" />
    );
  };

  const thCls = 'px-4 py-2 text-left text-xs font-medium text-muted-foreground cursor-pointer select-none hover:text-foreground';

  return (
    <div className="rounded-xl border bg-card shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className={thCls} onClick={() => toggleSort('observationDate')}>
                Date <SortIcon col="observationDate" />
              </th>
              <th className={thCls} onClick={() => toggleSort('siteName')}>
                Site <SortIcon col="siteName" />
              </th>
              <th className={thCls} onClick={() => toggleSort('observerName')}>
                Observer <SortIcon col="observerName" />
              </th>
              <th className={thCls} onClick={() => toggleSort('childrenPresent')}>
                Children <SortIcon col="childrenPresent" />
              </th>
              <th className={thCls} onClick={() => toggleSort('totalAverage')}>
                Total Avg <SortIcon col="totalAverage" />
              </th>
              <th className={thCls} onClick={() => toggleSort('scoredItems')}>
                Scored <SortIcon col="scoredItems" />
              </th>
              <th className={thCls} onClick={() => toggleSort('naItems')}>
                NA <SortIcon col="naItems" />
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">
                Notes
              </th>
              <th className="px-4 py-2 text-center text-xs font-medium text-muted-foreground">
                Detail
              </th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
                  No observations match the current filters.
                </td>
              </tr>
            ) : (
              paginated.map((o) => (
                <tr
                  key={o.recordId}
                  className="cursor-pointer border-b last:border-0 hover:bg-muted/50"
                  onClick={() => setSelected(o)}
                >
                  <td className="whitespace-nowrap px-4 py-2.5">{o.observationDate}</td>
                  <td className="px-4 py-2.5 font-medium">{o.siteName}</td>
                  <td className="px-4 py-2.5">{o.observerName}</td>
                  <td className="px-4 py-2.5 text-center">{o.childrenPresent}</td>
                  <td className="px-4 py-2.5 text-center">{scoreBadge(o.totalAverage)}</td>
                  <td className="px-4 py-2.5 text-center">{o.scoredItems}</td>
                  <td className="px-4 py-2.5 text-center">{o.naItems}</td>
                  <td className="max-w-[200px] truncate px-4 py-2.5 text-xs text-muted-foreground">
                    {o.notes || '—'}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected(o);
                      }}
                      className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <Eye className="h-3 w-3" />
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Detail drawer */}
      {selected && (
        <ObservationDrawer observation={selected} onClose={() => setSelected(null)} />
      )}

      {/* Pagination */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-2.5 text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <span>Rows per page:</span>
          {PAGE_SIZES.map((s) => (
            <button
              key={s}
              onClick={() => {
                setPageSize(s);
                setPage(0);
              }}
              className={`rounded px-2 py-0.5 ${pageSize === s ? 'bg-dece-navy text-white' : 'hover:bg-muted'}`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span>
            Page {page + 1} of {totalPages}
          </span>
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            className="rounded border px-2 py-0.5 disabled:opacity-40 hover:bg-muted"
          >
            Prev
          </button>
          <button
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
            className="rounded border px-2 py-0.5 disabled:opacity-40 hover:bg-muted"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
